import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiURL } from "../lib/api";
import { useOrg } from "../lib/session";
import { useWords } from "../lib/workspace";
import type { Brand } from "../lib/brand";
import type { BookingService } from "../lib/types";
import { BrandEditor } from "../components/BrandEditor";
import { ErrorText, Field, Loading, PageHeader } from "../components/ui";

interface BrandView { brand: Brand; logo_url: string }

interface Page {
  slug: string; enabled: boolean; headline: string; intro: string; headline_ar: string; intro_ar: string;
  default_language: "en" | "ar"; service_ids: string[]; choose_staff: boolean; require_email: boolean; ask_notes: boolean;
  embed_origins: string[]; privacy_url: string | null; access: "link" | "website";
}
interface PageView { page: Page; exists: boolean; link: string; embed_snippet: string; services: BookingService[]; enabled_in_plan: boolean; can_edit_embed: boolean; guessable: boolean }

// BrandAndBookingPage: the company's look (used by the chat widget and the
// booking page) and its public booking page.
export function BrandSettingsPage() {
  const { allows } = useOrg();
  return (
    <>
      <PageHeader title="Brand" />
      <BrandSection canEdit={allows("workspace")} />
    </>
  );
}

export function BookingPageSettings() {
  return (
    <>
      <PageHeader title="Booking page" />
      <BookingPageSection />
    </>
  );
}

function BrandSection({ canEdit }: { canEdit: boolean }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [orgId, "brand"], queryFn: () => api<BrandView>(`${base}/brand`) });
  const [b, setB] = useState<Brand | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (q.data && !b) setB(q.data.brand); }, [q.data, b]);
  if (q.isLoading || !b) return <Loading />;
  const logoBase = q.data!.logo_url ? q.data!.logo_url.replace(/[0-9a-f]{64}\.png$/, "") : apiURL("/v1/public/logos/");
  return (
    <section className="card">
      <h2>Brand</h2>
      <p className="muted small">Your colors, font and logo on the chat widget and the booking page. {canEdit ? "" : "Only admins can change them."}</p>
      <BrandEditor value={b} disabled={!canEdit} onChange={(x) => { setB(x); setSaved(false); }} logoURL={(h) => logoBase + h + ".png"}
        uploadLogo={async (file) => {
          const fd = new FormData();
          fd.append("file", file);
          const out = await api<{ logo: string }>(`${base}/brand/logo`, { method: "POST", body: fd });
          return out.logo;
        }} />
      <ErrorText error={error} />
      {canEdit && (
        <div className="inline" style={{ marginTop: ".75rem" }}>
          <button disabled={busy} onClick={async () => {
            setBusy(true); setError(null);
            try {
              const out = await api<BrandView>(`${base}/brand`, { method: "PUT", json: { brand: b } });
              setB(out.brand); setSaved(true);
              qc.invalidateQueries({ queryKey: [orgId, "brand"] });
            } catch (err) { setError(err); } finally { setBusy(false); }
          }}>{busy ? "Saving…" : "Save brand"}</button>
          {saved && <span className="muted small">Saved. The widget and booking page use it now.</span>}
        </div>
      )}
    </section>
  );
}

function BookingPageSection() {
  const { base, orgId } = useOrg();
  const w = useWords();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [orgId, "booking-page"], queryFn: () => api<PageView>(`${base}/booking-page`) });
  const [p, setP] = useState<Page | null>(null);
  const [origins, setOrigins] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [previewKey, setPreviewKey] = useState(0);
  useEffect(() => {
    if (q.data && !p) { setP(q.data.page); setOrigins(q.data.page.embed_origins.join("\n")); }
  }, [q.data, p]);
  if (q.isLoading || !p) return <Loading />;
  const v = q.data!;
  const set = (patch: Partial<Page>) => { setP({ ...p, ...patch }); setSaved(false); };
  const copy = async (text: string, what: string) => { try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(""), 1500); } catch { /* ignore */ } };
  const live = v.exists && v.page.enabled;
  return (
    <section className="card">
      <h2>Booking page</h2>
      {!v.enabled_in_plan && <p className="notice small">The booking page isn't part of your plan yet. Contact SVENEX to add it.</p>}
      <p className="muted small">A page where customers book themselves. Share the link, or put it on your website with one line of code.</p>
      <label className="check"><input type="checkbox" checked={p.enabled} disabled={!v.enabled_in_plan} onChange={(e) => set({ enabled: e.target.checked })} /> Page is live</label>
      <div className="row">
        <Field label="Address">
          <span className="slug-field"><code className="small">{v.link}</code></span>
          {v.exists && v.can_edit_embed && (
            <button type="button" className="link small" disabled={busy} onClick={async () => {
              if (!window.confirm("Make a new address? The current link and website code stop working, and you'll need to share the new ones.")) return;
              setBusy(true); setError(null);
              try {
                const out = await api<PageView>(`${base}/booking-page`, { method: "PUT", json: { ...v.page, new_address: true } });
                qc.setQueryData([orgId, "booking-page"], out);
                setP(out.page); setSaved(true); setPreviewKey((k) => k + 1);
              } catch (err) { setError(err); } finally { setBusy(false); }
            }}>Make a new address</button>
          )}
        </Field>
        <Field label="Page language (visitors can switch)">
          <select value={p.default_language} onChange={(e) => set({ default_language: e.target.value as Page["default_language"] })}>
            <option value="en">English</option><option value="ar">العربية</option>
          </select>
        </Field>
      </div>
      <div className="row">
        <Field label="Headline"><input value={p.headline} maxLength={120} onChange={(e) => set({ headline: e.target.value })} /></Field>
        <Field label="Headline in Arabic"><input dir="rtl" value={p.headline_ar} maxLength={120} onChange={(e) => set({ headline_ar: e.target.value })} /></Field>
      </div>
      <div className="row">
        <Field label="Short introduction"><textarea rows={2} maxLength={600} value={p.intro} onChange={(e) => set({ intro: e.target.value })} /></Field>
        <Field label="Introduction in Arabic"><textarea dir="rtl" rows={2} maxLength={600} value={p.intro_ar} onChange={(e) => set({ intro_ar: e.target.value })} /></Field>
      </div>
      <div className="field">
        <span>{w("service", true)} on the page (none ticked = all)</span>
        <div className="checks">
          {v.services.map((s) => (
            <label key={s.id}><input type="checkbox" checked={p.service_ids.includes(s.id)}
              onChange={(e) => set({ service_ids: e.target.checked ? [...p.service_ids, s.id] : p.service_ids.filter((x) => x !== s.id) })} /> {s.name}</label>
          ))}
          {v.services.length === 0 && <span className="muted small">Add {w.lower("service", true)} under Calendar first.</span>}
        </div>
      </div>
      <div className="checks" style={{ margin: ".5rem 0 .75rem" }}>
        <label><input type="checkbox" checked={p.choose_staff} onChange={(e) => set({ choose_staff: e.target.checked })} /> Customers can pick the {w.lower("resource")}</label>
        <label><input type="checkbox" checked={p.require_email} onChange={(e) => set({ require_email: e.target.checked })} /> Email is required</label>
        <label><input type="checkbox" checked={p.ask_notes} onChange={(e) => set({ ask_notes: e.target.checked })} /> Ask for a note</label>
      </div>
      <div className="row">
        <Field label="Your privacy policy (link shown on the form)"><input type="url" placeholder="https://www.yourcompany.com/privacy" value={p.privacy_url ?? ""} maxLength={300} onChange={(e) => set({ privacy_url: e.target.value || null })} /></Field>
        <Field label={`Websites that may show the page${v.can_edit_embed ? "" : " (admins only)"}`}>
          <textarea rows={2} disabled={!v.can_edit_embed} placeholder="https://www.yourcompany.com" value={origins} onChange={(e) => { setOrigins(e.target.value); setSaved(false); }} />
        </Field>
      </div>
      <div className="field">
        <span>Who can open the page{v.can_edit_embed ? "" : " (admins only)"}</span>
        <label className="check"><input type="radio" name="access" checked={p.access !== "website"} disabled={!v.can_edit_embed} onChange={() => set({ access: "link" })} />
          Anyone with the link</label>
        <label className="check"><input type="radio" name="access" checked={p.access === "website"} disabled={!v.can_edit_embed} onChange={() => set({ access: "website" })} />
          Only on our website (the link opens nowhere else; add your website above)</label>
      </div>
      {v.guessable && <p className="notice small">This page's address is based only on your name, so it's easy to guess. Use "Make a new address" for an unguessable one.</p>}
      <ErrorText error={error} />
      <div className="inline">
        <button disabled={busy} onClick={async () => {
          setBusy(true); setError(null);
          try {
            const out = await api<PageView>(`${base}/booking-page`, { method: "PUT", json: { ...p, embed_origins: origins.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean) } });
            qc.setQueryData([orgId, "booking-page"], out);
            setP(out.page); setOrigins(out.page.embed_origins.join("\n")); setSaved(true); setPreviewKey((k) => k + 1);
          } catch (err) { setError(err); } finally { setBusy(false); }
        }}>{busy ? "Saving…" : "Save booking page"}</button>
        {saved && <span className="muted small">Saved.</span>}
      </div>

      {live && (
        <div className="share">
          <h3>Share it</h3>
          {v.page.access === "website" ? <p className="muted small">The page opens only on your website, so there's no link to share.</p> : (
            <div className="inline"><code className="code-line">{v.link}</code><button className="secondary" onClick={() => copy(v.link, "link")}>{copied === "link" ? "Copied" : "Copy link"}</button>
              <a className="button-link" href={v.link} target="_blank" rel="noreferrer">Open</a></div>)}
          <p className="muted small" style={{ margin: ".75rem 0 .25rem" }}>To show it on your website, paste this where the booking form should appear (your site must be listed above):</p>
          <div className="inline"><code className="code-line">{v.embed_snippet}</code><button className="secondary" onClick={() => copy(v.embed_snippet, "embed")}>{copied === "embed" ? "Copied" : "Copy code"}</button></div>
          <h3 style={{ marginTop: "1.25rem" }}>Preview</h3>
          <iframe key={previewKey} className="booking-preview" src={`${v.link}?embed=1`} title="Booking page preview" />
        </div>
      )}
    </section>
  );
}
